/**
 * AtlasOnboardingModal — first-visit welcome overlay for /knowledge-atlas.
 *
 * ⚠️  COPY-GUARDED FILE  —  see /app/memory/COPY_GOVERNANCE.md
 * ---------------------------------------------------------------------
 *   Every user-facing string in this component is under the Copy Judge.
 *   Baseline id: `atlas-onboarding-modal-v3` (approved by operator 2026-02-10).
 *
 *   Before editing any JSX text / title / aria-label / placeholder here:
 *     python /app/scripts/copy_guard.py check <this file>
 *   After editing, before claiming the change is shipped:
 *     python /app/scripts/copy_guard.py diff  <this file>
 *   and paste the diff to the operator for a [COPY-APPROVED] handshake.
 * ---------------------------------------------------------------------
 *
 * Behaviour:
 *   • Auto-opens the first time the user lands on the Atlas (localStorage
 *     key `atlas-onboarding-seen` absent).
 *   • Re-openable from the "(i)" info pin in the page header.
 *   • Two-column body — Section 1 (memory) + Section 2 (what you can
 *     do with it + moved nightly refresh paragraph). Section 3 was
 *     removed 2026-02-10 to reduce cognitive load; its paragraph now
 *     lives under the Section 2 bullet list.
 *   • Custom icons (Brain, Sparkles, Shield) live in
 *     /app/frontend/public/onboarding-icons/. Hover any icon to see
 *     it enlarge with a soft glow.
 *   • Amber "THIS IS IMPORTANT" privacy callout — data-access consent
 *     framework itself is not built for beta (post-beta P2).
 *
 * Every interactive element carries a data-testid.
 */
import React from 'react';
import { X } from 'lucide-react';

const ONBOARDING_KEY = 'atlas-onboarding-seen';

const ICON_BRAIN    = '/onboarding-icons/brain.png';
const ICON_SPARKLES = '/onboarding-icons/sparkles.png';
const ICON_SHIELD   = '/onboarding-icons/shield.png';

// D6-off-PDF (2026-02-13) — operator directive: "I logged in a new user
//  > Clicked on KA > Did not receive 1st-time user information page
//  explaining KA". Root cause: the onboarding-seen flag lived in
// localStorage, which is shared across every user account on the same
// browser. A brand-new user re-using the same browser inherited the
// prior user's "seen" flag and got skipped past the onboarding.
//
// Fix: swap to sessionStorage so a fresh browser session (new tab, new
// window, or re-login on the same day) always sees the onboarding.
// Legacy localStorage flag is explicitly removed on mark-seen so old
// state from previous builds doesn't linger and suppress Step 1.
export const hasSeenAtlasOnboarding = () => {
  try {
    return sessionStorage.getItem(ONBOARDING_KEY) === 'true';
  } catch {
    return false;
  }
};

export const markAtlasOnboardingSeen = () => {
  try {
    sessionStorage.setItem(ONBOARDING_KEY, 'true');
    // Clear the pre-D6 localStorage flag so returning users on stale
    // builds start clean the next time they land on /knowledge-atlas.
    localStorage.removeItem(ONBOARDING_KEY);
  } catch {
    /* private-mode; ignore */
  }
};

export const clearAtlasOnboardingFlag = () => {
  try {
    sessionStorage.removeItem(ONBOARDING_KEY);
    localStorage.removeItem(ONBOARDING_KEY);
  } catch {
    /* private-mode; ignore */
  }
};

export const AtlasOnboardingModal = ({ open, onDismiss }) => {
  if (!open) return null;

  const handleDismiss = () => {
    markAtlasOnboardingSeen();
    if (typeof onDismiss === 'function') onDismiss();
  };

  return (
    <div
      data-testid="atlas-onboarding-backdrop"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 px-4 py-8 backdrop-blur-sm"
      onClick={handleDismiss}
      role="dialog"
      aria-modal="true"
      aria-labelledby="atlas-onboarding-title"
    >
      <div
        data-testid="atlas-onboarding-modal"
        className="relative flex max-h-[calc(100vh-4rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-cyan-400/40 bg-slate-950/95 shadow-[0_0_60px_rgba(34,211,238,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          data-testid="atlas-onboarding-close"
          onClick={handleDismiss}
          className="absolute right-4 top-4 z-10 rounded-full p-1.5 text-cyan-300/70 transition-colors hover:bg-cyan-400/10 hover:text-cyan-100"
          aria-label="Close welcome"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Scroll area — SPD v5.76 (2026-07-12) — D1-defect-3 fix.
            Previously the modal used `overflow-visible` with no height
            cap, so on shorter viewports the top and bottom would clip
            outside the screen and the "Got it" footer became
            unreachable. Now the modal caps at `max-h-[calc(100vh-4rem)]`
            (see wrapper above) and everything above the footer scrolls
            inside this div. The footer stays sticky at the bottom of
            the visible area so the CTA is always in reach. */}
        <div
          data-testid="atlas-onboarding-scroll"
          className="flex-1 overflow-y-auto overscroll-contain"
        >

        {/* Header */}
        <div className="border-b border-cyan-400/20 px-8 pt-10 pb-6">
          <div className="mb-3 flex items-center gap-3">
            <span
              data-testid="atlas-onboarding-header-icon"
              className="group relative inline-flex h-10 w-10 items-center justify-center rounded-lg border border-cyan-400/50 bg-cyan-500/10"
            >
              <img
                src={ICON_BRAIN}
                alt=""
                className="h-5 w-5 origin-center transition-transform duration-200 ease-out group-hover:z-50 group-hover:scale-[3.2] group-hover:drop-shadow-[0_6px_18px_rgba(103,232,249,0.55)]"
              />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-400/80">
              First-visit briefing
            </span>
          </div>

          <h1
            id="atlas-onboarding-title"
            data-testid="atlas-onboarding-title"
            className="text-[32px] font-semibold leading-tight text-cyan-50"
          >
            Welcome to Your Knowledge Atlas.
          </h1>

          <p
            data-testid="atlas-onboarding-subtitle"
            className="mt-2 text-lg leading-relaxed text-cyan-200/85"
          >
            The Knowledge Atlas is a <strong className="text-cyan-100">self-learning
            memory system</strong> for your documents, or any other information
            you wish to upload. The Atlas learns and remembers.
          </p>

          {/* Privacy callout — amber, Shield icon */}
          <div
            data-testid="atlas-onboarding-privacy-callout"
            className="mt-5 grid grid-cols-[32px_1fr] items-start gap-4 rounded-xl border border-amber-400/80 bg-amber-500/10 px-5 py-4"
          >
            <span className="group relative mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-amber-400/60 bg-amber-500/15">
              <img
                src={ICON_SHIELD}
                alt=""
                className="h-5 w-5 origin-center transition-transform duration-200 ease-out group-hover:z-50 group-hover:scale-[3.4] group-hover:drop-shadow-[0_6px_18px_rgba(251,191,36,0.6)]"
              />
            </span>
            <p className="text-[13px] leading-relaxed text-slate-100">
              <strong className="font-semibold uppercase tracking-wide text-amber-300">
                This is important.
              </strong>{' '}
              What you choose to upload here is yours and yours alone. We cannot
              see or read it without your explicit permission and approval.
              Giving us your permission allows us to provide a better and more
              robust Atlas over time. The choice is yours. Uploading new
              documents is currently not available for beta. However we have
              uploaded several factual documents on the Search and AI Agent
              category. Check out the difference between Google and Search.Inc.
            </p>
          </div>
        </div>

        {/* Body — SPD v5.76 (2026-07-12) — D1-defect-4.
            Previously used a 2-column grid at md+ which put the two
            sections side by side. Operator directive: put "What you can
            do with it" BELOW "A self-learning memory that survives every
            session" on every viewport. Switched to a single-column
            layout with vertical rhythm. */}
        <div className="flex flex-col gap-8 px-8 py-8">
          {/* SECTION 1 */}
          <section data-testid="atlas-onboarding-section-what" className="space-y-3">
            <div className="flex items-center gap-2">
              <span
                data-testid="atlas-onboarding-section-1-icon"
                className="group relative inline-flex h-4 w-4 items-center justify-center"
              >
                <img
                  src={ICON_BRAIN}
                  alt=""
                  className="h-4 w-4 origin-center transition-transform duration-200 ease-out group-hover:z-50 group-hover:scale-[5] group-hover:drop-shadow-[0_6px_18px_rgba(103,232,249,0.55)]"
                />
              </span>
              <h3 className="text-[15px] font-semibold text-cyan-300">
                A self-learning memory that survives every session
              </h3>
            </div>
            <p className="text-lg leading-relaxed text-cyan-200/85">
              Every document or artefact you add is permanently woven into a
              single evolving map of{' '}
              <strong className="text-cyan-100">Topics</strong>,{' '}
              <strong className="text-cyan-100">Sub-Topics</strong> and{' '}
              <strong className="text-cyan-100">Concepts</strong>. The Atlas
              becomes smarter and remembers as more information is provided.
              No re-ingestion cost, no context loss, no drift.
            </p>
          </section>

          {/* SECTION 2 · now includes the moved paragraph */}
          <section data-testid="atlas-onboarding-section-what-for" className="space-y-3">
            <div className="flex items-center gap-2">
              <span
                data-testid="atlas-onboarding-section-2-icon"
                className="group relative inline-flex h-4 w-4 items-center justify-center"
              >
                <img
                  src={ICON_SPARKLES}
                  alt=""
                  className="h-4 w-4 origin-center transition-transform duration-200 ease-out group-hover:z-50 group-hover:scale-[5] group-hover:drop-shadow-[0_6px_18px_rgba(103,232,249,0.55)]"
                />
              </span>
              <h3 className="text-[15px] font-semibold text-cyan-300">
                What you can do with it
              </h3>
            </div>
            <ul className="ml-4 list-disc space-y-1.5 text-lg leading-relaxed text-cyan-200/85">
              <li>Use the information to create new and better artefacts.</li>
              <li>Ask plain-English questions across the whole map.</li>
              <li>Trace how any two or more Concepts relate.</li>
              <li>Spot yesterday&apos;s contradictions before anyone else does.</li>
              <li>Every Concept has its own evidence-anchored page.</li>
              <li>Access source documents, artefacts and code held in the Knowledge Graph.</li>
              <li>See new connections made between Sub-Topics and Concepts when new information is added.</li>
            </ul>
            <p
              data-testid="atlas-onboarding-nightly-refresh-note"
              className="mt-4 border-t border-dashed border-cyan-400/20 pt-3 text-[12px] italic leading-relaxed text-cyan-300/75"
            >
              Every night the Atlas re-reads what has changed, draws new
              Connections, updates existing Concepts, and — where it can —
              proactively surfaces opportunities and pitfalls. Prior
              understanding is preserved: nothing is re-learned from scratch.
            </p>
          </section>
        </div>

        </div>
        {/* /Scroll area */}

        {/* Footer CTA — sticky at bottom of the visible modal frame */}
        <div className="flex shrink-0 items-center justify-between gap-4 border-t border-cyan-400/20 bg-slate-950/95 px-8 py-5">
          <span className="font-mono text-[10px] uppercase tracking-widest text-cyan-400/50">
            You can re-open this from the (i) pin in the header.
          </span>
          <button
            type="button"
            data-testid="atlas-onboarding-dismiss-btn"
            onClick={handleDismiss}
            className="inline-flex items-center gap-2 rounded-lg border border-cyan-400/60 bg-cyan-500/20 px-5 py-2.5 text-sm font-medium text-cyan-50 transition-all hover:border-cyan-300 hover:bg-cyan-400/30"
          >
            Got it — take me to the map
          </button>
        </div>
      </div>
    </div>
  );
};

export default AtlasOnboardingModal;
